import {defineStore} from 'pinia';
import {getFromAny} from "~/composables/getFromAny.js";
import {useBreadcrumbStore} from "~/stores/BreadcrumbStore.js";
import { useLocalePath } from '#imports';

export const useCatalogStore = defineStore('catalog', () => {
    const catalogResource = ref({});
    const quantitiesStore = useProductQuantityStore();
    const mobileSearch = ref(false);

    const products = computed(() => {
        return catalogResource.value.products.map((product) => {
            quantitiesStore.setQuantity(product.id, product.quantity);
            useProductTierPricing(product);

            return product;
        });
    });

    const catalog = computed(() => catalogResource.value);
    const filters = computed(() => catalog.value.filters);
    const paginator = computed(() => catalogResource.value.paginator);

    const filtersSelected = computed(() => {
        const raw = catalog.value.filters_selected

        if (!raw || typeof raw !== 'object') {
            return {}
        }

        return Object.fromEntries(
            Object.entries(raw).map(([key, val]) => [
                key,
                Array.isArray(val)
                    ? val.map(inner => String(inner))
                    : String(val)
            ])
        )
    })
    const isLoading = ref(false);
    const sortingOptions = ref({
        'name_asc': 'catalog.name_asc',
        'name_desc': 'catalog.name_desc',
        'fine_weight_asc': 'catalog.fine_weight_asc',
        'fine_weight_desc': 'catalog.fine_weight_desc',
        'price_asc': 'catalog.price_asc',
        'price_desc': 'catalog.price_desc',
        'is_popular_desc': 'catalog.is_popular_desc',
        'best_price_per_oz_asc': 'catalog.best_price_per_oz_asc',
        'introduced_since_asc': 'catalog.introduced_since_asc',
        'introduced_since_desc': 'catalog.introduced_since_desc',
    });
    const sortingLabel = computed(() => sortingOptions.value[getOption('sorting')]);


     async function fetchCatalog(term, opts) {
        isLoading.value = true;
         const queryParams = useRoute().query;

         const filteredQueryParams = Object.fromEntries(
             Object.entries(queryParams).filter(([key]) => key !== 'url')
         );

         const queryString = new URLSearchParams(filteredQueryParams).toString();
         const url = `catalog?${queryString}`;
         const localePath = useLocalePath();


         const filtersSelected = catalog.value.filters_selected;

         const body = {
             ...filtersSelected,
             ...catalogResource.value.options,
             page: paginator.value?.current_page,
             update_sorting: true,
             term,
         }

         if (!opts?.updating_rates) {
             delete body['url'];
         }

         if (window) {
             const keysToCheck = [
                 'type_ids',
                 'metal_ids',
                 'series_ids',
                 'country_ids',
                 'misc',
                 'mint_ids'
             ];
             const noneSelected = keysToCheck.every(
                 key => !(filtersSelected[key]?.length)
             );
             if (noneSelected &&
                 window.location.pathname !== (localePath({name: 'index'}) + '/') &&
                 window.location.pathname !== (localePath({name: 'search'}) + '/')) {
                 body.url = window.location.pathname;
             }
         }
         return await getFromAny(url, {
             method: 'POST',
             body
         }).then(data => {
            isLoading.value = false;
            catalogResource.value = data.catalog;

            //update URL

            updateUrl();

            if (process.client && data.meta) {
                const { title, description, hreflang, og } = data.meta;
                const headOptions = {
                    title,
                    description,
                    link: [
                        {
                            rel: 'canonical',
                            href: data.catalog.url,
                        },
                        ...hreflang,
                    ],
                    meta: [
                        {
                            property: 'og:url',
                            content: data.catalog.url,
                        },
                        ...og
                    ],
                };
                useHead(headOptions);
            }

            if (data.breadcrumbs) {
                const breadcrumbStore = useBreadcrumbStore();
                data.breadcrumbs.forEach((item, index) => {
                    breadcrumbStore.items[index] = item;
                });
            }

            return data;
        });
    }

    function updateUrl() {
        if (!catalogResource.value) {
            return;
        }
        let url = catalogResource.value.url;

        // Add page param
        if (paginator.value.current_page > 1) {
            const operator = url.includes('?') ? '&' : '?';
            url += operator + 'page=' + encodeURIComponent(paginator.value.current_page);
        }

        if (process.client) {
            if (catalogResource.value.teaser_title) {
                window.document.title = catalogResource.value.teaser_title;
            }
            window.history.replaceState(window.history.state, null, url);
        }
    }

    async function setSorting(sorting) {

        await setOption('sorting', sorting);
        paginator.value.current_page = 1;

        fetchCatalog();
        // this.hideMobileFilters();
    }

    function setView(view) {
        setOption('view', view);
    }

    async function storeOptions(opts) {
        await useClientFetch('catalog/options', {
            method: 'POST',
            body: opts
        });
    }

    async function setOption(key, value) {
        catalogResource.value.options[key] = value;

        await storeOptions({[key]:value});
    }

    function getOption(key) {
         if (!catalogResource.value?.options) {
             return;
         }

        return key in catalogResource.value.options ? catalogResource.value.options[key] : null;
    }

    const toggleMobileSearch = () => {
        mobileSearch.value = !mobileSearch.value;
        window.scrollTo({top: 0, left: 0, behavior: 'instant'});
    };

    return {
        catalogResource,
        filters,
        fetchCatalog,
        setSorting,
        setView,
        getOption,
        sortingOptions,
        sortingLabel,
        catalog,
        products,
        paginator,
        isLoading,
        mobileSearch,
        toggleMobileSearch,
        filtersSelected,
    };
});
